import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, X } from "lucide-react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function WarningBadge() {
  const { user } = useAuth();
  const [warnings, setWarnings] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user || !user.id) return;
    try {
      const { data } = await API.get("/warnings");
      setWarnings(data.warnings || []);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!warnings.length) return null;

  const dismiss = async (id) => {
    setBusy(true);
    try {
      await API.post(`/warnings/${id}/dismiss`);
      setWarnings((prev) => prev.filter((w) => w.warning_id !== id));
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  const dismissAllAndClose = async () => {
    setBusy(true);
    try {
      await Promise.all(warnings.map((w) => API.post(`/warnings/${w.warning_id}/dismiss`)));
      setWarnings([]);
      setOpen(false);
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="warning-badge"
        title={`You have ${warnings.length} warning${warnings.length > 1 ? "s" : ""} from admin`}
        className="relative p-2 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] hover:bg-[#F59E0B]/20 transition-all animate-pulse"
      >
        <AlertTriangle className="w-4 h-4" />
        {warnings.length > 1 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#EF4444] text-white rounded-full text-[9px] font-bold flex items-center justify-center">
            {warnings.length > 9 ? "9+" : warnings.length}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="warning-modal">
          <div className="bg-[#131722] border border-[#F59E0B]/40 rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F59E0B]/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#F59E0B]">Warning from Admin</h3>
                  <p className="text-xs text-[#94A3B8]">Please read carefully</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#64748B] hover:text-[#F8FAFC]" data-testid="warning-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {warnings.map((w) => (
                <div key={w.warning_id} className="bg-[#0B0E14] border border-[#F59E0B]/20 rounded-lg p-4" data-testid={`warning-${w.warning_id}`}>
                  <p className="text-sm text-[#F8FAFC] whitespace-pre-wrap break-words">{w.message}</p>
                  <div className="flex items-center justify-between mt-3 text-[10px] text-[#64748B]">
                    <span>{new Date(w.created_at).toLocaleString()}</span>
                    <button
                      onClick={() => dismiss(w.warning_id)}
                      disabled={busy}
                      data-testid={`warning-dismiss-${w.warning_id}`}
                      className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30 rounded hover:bg-[#F59E0B]/20 disabled:opacity-50"
                    >
                      I Understand
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {warnings.length > 1 && (
              <button
                onClick={dismissAllAndClose}
                disabled={busy}
                data-testid="warning-dismiss-all-btn"
                className="w-full mt-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#F59E0B] text-[#0B0E14] rounded-lg hover:bg-[#F59E0B]/90 disabled:opacity-50"
              >
                I Understand All ({warnings.length})
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
